import { useState, useEffect, useCallback, useRef } from 'react';
import { DPEAPI } from '@/services/api';
export type AnalysisStatus =
  | 'pending'
  | 'fetching'
  | 'analyzing'
  | 'generating_questions'
  | 'completed'
  | 'error';

interface AnalysisStatusState {
  status: AnalysisStatus;
  progress: number;
  data: any | null;
  error: string | null;
  analysisId: string | null;
  questionnaireId: string | null;
}

export function useAnalysisStatus(analysisId: string) {
  const [state, setState] = useState<AnalysisStatusState>({
    status: 'pending',
    progress: 0,
    data: null,
    error: null,
    analysisId: analysisId,
    questionnaireId: null
  });

  const [polling, setPolling] = useState(true);
  const pollIntervalRef = useRef(3000);
  const isMountedRef = useRef(true);
  const pollingRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    pollingRef.current = polling;
  }, [polling]);

  const checkStatus = useCallback(async () => {
    if (!isMountedRef.current || !pollingRef.current) return;

    try {
      const data = await DPEAPI.getStatus(analysisId);

      if (!isMountedRef.current) return;

      setState(prev => ({
        ...prev,
        status: data.status,
        progress: data.progress || prev.progress,
        data: data.data || prev.data,
        questionnaireId: data.questionnaire_id || prev.questionnaireId
      }));

      if (data.status === 'completed' || data.status === 'error') {
        setPolling(false);

        if (data.status === 'error') {
          setState(prev => ({
            ...prev,
            error: data.error || 'Une erreur est survenue lors de l\'analyse'
          }));
        }
        return;
      }

      if (data.status === 'fetching') {
        pollIntervalRef.current = 2000;
      } else if (data.status === 'analyzing') {
        pollIntervalRef.current = 3000;
      }

    } catch (error) {
      console.error('Error checking analysis status:', error);

      if (!isMountedRef.current) return;

      setState(prev => ({
        ...prev,
        progress: Math.min(prev.progress + 5, 99)
      }));
    }
  }, [analysisId]);

  useEffect(() => {
    if (!polling) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const poll = async () => {
      await checkStatus();
      if (!cancelled && isMountedRef.current && pollingRef.current) {
        timeoutId = setTimeout(poll, pollIntervalRef.current);
      }
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [checkStatus, polling]);

  const retry = useCallback(async () => {
    setState(prev => ({
      ...prev,
      status: 'pending',
      progress: 0,
      error: null
    }));

    setPolling(true);

    try {
      await DPEAPI.retry(analysisId);
    } catch (error) {
      console.error('Error retrying analysis:', error);
    }
  }, [analysisId]);

  const cancel = useCallback(async () => {
    setPolling(false);

    try {
      await DPEAPI.cancel(analysisId);

      setState(prev => ({
        ...prev,
        status: 'error',
        error: 'Analyse annulée par l\'utilisateur'
      }));
    } catch (error) {
      console.error('Error cancelling analysis:', error);
    }
  }, [analysisId]);

  return {
    ...state,
    retry,
    cancel,
    isPolling: polling
  };
}

export default useAnalysisStatus;
